import { BUILD_SHA, frontendErrorsUrl } from '@shared/api/config';
import { getLastRequestId } from '@shared/api/http';

import { scrub } from './scrub';

/**
 * The first-party error channel. **No third-party browser-error SDK in v1** —
 * no Sentry, no Bugsnag, no session replay, no product-analytics SDK.
 *
 * A small structured event is `POST`ed on unhandled errors and unhandled
 * promise rejections, and by the router's error boundaries — but only where
 * `VITE_FRONTEND_ERRORS_URL` names a target. **Unset disables the handler
 * entirely**, which is what it is on develop: no backend owns this route yet,
 * and this host serves no API, so every report was a 404.
 *
 * `app: "admin"` is what splits this stream from the two sibling frontends in
 * log search. `X-Request-ID` comes from the last response — nginx is the only
 * source of it, and this bundle never generates one.
 *
 * This console is a static bundle, so every failure is a browser failure and
 * this is the only reporting channel there is.
 */

const STACK_LIMIT = 4096;

export interface FrontendErrorEvent {
  app: 'admin';
  route: string;
  message: string;
  x_request_id: string | null;
  stack?: string;
  user_agent: string;
  locale: string;
  build_sha: string;
  metric_type?: 'web_vital';
  metric_name?: string;
  metric_value?: number;
}

function send(event: FrontendErrorEvent): void {
  // UNSET DISABLES THE CHANNEL ENTIRELY. Without this the console posts every
  // error to a route no backend owns and no host serves, so each report is a
  // 404 — and since a failed report is itself an error the browser can raise,
  // the cheapest failure mode here is a loop. Silence is the correct behaviour
  // until the endpoint has a declared owner.
  const url = frontendErrorsUrl();
  if (!url) return;

  const body = JSON.stringify(event);

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch(url, { method: 'POST', body, keepalive: true });
  } catch {
    // Reporting a failure must never become one.
  }
}

export function reportError(error: unknown, context: { route?: string } = {}): void {
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error && error.stack ? error.stack.slice(0, STACK_LIMIT) : undefined;

  send({
    app: 'admin',
    route: context.route ?? window.location.pathname,
    message: scrub(message),
    x_request_id: getLastRequestId(),
    ...(stack ? { stack: scrub(stack) } : {}),
    user_agent: navigator.userAgent,
    locale: navigator.language,
    build_sha: BUILD_SHA,
  });
}

export function reportWebVital(name: string, value: number): void {
  send({
    app: 'admin',
    route: window.location.pathname,
    message: `web-vital:${name}`,
    x_request_id: getLastRequestId(),
    user_agent: navigator.userAgent,
    locale: navigator.language,
    build_sha: BUILD_SHA,
    metric_type: 'web_vital',
    metric_name: name,
    metric_value: value,
  });
}

export function installGlobalErrorHandlers(): void {
  window.addEventListener('error', (event) => reportError(event.error ?? event.message));
  window.addEventListener('unhandledrejection', (event) => reportError(event.reason));
}
